import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditCellForm, type EditCellFormProps } from '~/components/ui/table';
import {
  InlineEditButton,
  MissingValueButton,
} from '~/components/ui/buttons/InlineValueButtons';
import { PiecesPerBundleLabel } from '~/components/ui/PiecesPerBundleLabel';
import { usePiecesPerBundleUnit } from '~/hooks/usePiecesPerBundleUnit';
import {
  MIN_PIECES_PER_BUNDLE,
  MAX_PIECES_PER_BUNDLE,
  bundleSizeMessageParams,
  parseBundleSize,
} from '~/utils/bundleBounds';

type PiecesPerBundleEditorProps = {
  piecesPerBundle: number;
  linePieces: number;
  onChange: (value: number) => void;
  onClear?: () => void;
  disabled?: boolean;
};

export function PiecesPerBundleEditor({
  piecesPerBundle,
  linePieces,
  onChange,
  onClear,
  disabled = false,
}: Readonly<PiecesPerBundleEditorProps>) {
  const { t } = useTranslation('cart');
  const unit = usePiecesPerBundleUnit();
  const [isEditing, setIsEditing] = useState(false);
  const hasBundleSize = piecesPerBundle > 0;

  const handleSubmit: EditCellFormProps['onSubmit'] = (raw, refuse) => {
    if (raw.trim() === '' && onClear) {
      onClear();
      setIsEditing(false);
      return;
    }
    const parsed = parseBundleSize(raw);
    if (!parsed.valid) {
      refuse(t('cart.pieces_per_bundle_error', bundleSizeMessageParams));
      return;
    }
    onChange(parsed.value);
    setIsEditing(false);
  };

  const editor = isEditing && (
    <EditCellForm
      defaultValue={hasBundleSize ? piecesPerBundle : ''}
      editableType="number"
      min={MIN_PIECES_PER_BUNDLE}
      max={MAX_PIECES_PER_BUNDLE}
      step={1}
      onSubmit={handleSubmit}
      onCancel={() => setIsEditing(false)}
      popupPosition="above"
      testIds={{
        input: 'pieces-per-bundle-input',
        submit: 'pieces-per-bundle-submit',
        cancel: 'pieces-per-bundle-cancel',
      }}
    />
  );

  if (!hasBundleSize) {
    return (
      <div className="relative mt-1">
        <MissingValueButton
          label={t('cart.add_pieces_per_bundle', { unit })}
          testId="add-pieces-per-bundle-button"
          onClick={() => setIsEditing(true)}
          disabled={disabled}
        />
        {editor}
      </div>
    );
  }

  return (
    <div className="relative flex items-center gap-1">
      <PiecesPerBundleLabel
        piecesPerBundle={piecesPerBundle}
        linePieces={linePieces}
      />
      <InlineEditButton
        label={t('cart.edit_pieces_per_bundle')}
        testId="edit-pieces-per-bundle-button"
        onClick={() => setIsEditing(true)}
        disabled={disabled}
      />
      {editor}
    </div>
  );
}
